import React, { Fragment, useRef } from 'react'
import { useParallaxCalculation } from '../../hooks/useParallax'
import DynamicLink from '../atoms/DynamicLink'
import InlinePlzTool from '../organisms/InlinePlzTool'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { useWindowSize } from 'react-use'

const M02TeaserNews = ({ newsItems, anchorLinkId }) => {
  const { width: windowWidth } = useWindowSize(
    typeof window !== 'undefined' ? window.innerWidth : 640,
  )
  const isMobile = windowWidth < 768
  const ref1 = useRef(null)
  const ref2 = useRef(null)
  const { yRef1, yRef2, recalculateParallax } = useParallaxCalculation({
    ref1,
    ref2,
  })

  return (
    <section className="my-20 md:my-28" id={anchorLinkId}>
      <div className="container">
        <div
          className={`${newsItems.length > 1 ? 'space-y-20' : 'space-y-8'} ${
            newsItems.length === 1 ? 'row-type-e' : 'row-type-a'
          } md:space-y-0 ${
            newsItems.length === 1 && newsItems[0].image.length === 0
              ? 'row-type-e-start'
              : ''
          }`}
        >
          <motion.div
            className={`${
              newsItems.length === 1 ? 'col-1-type-e' : 'col-1-type-a'
            }`}
            ref={ref1}
            style={{ y: newsItems.length === 1 ? yRef1 : 0 }}
          >
            {newsItems
              .filter((_, index) => index % 2 === 0)
              .map(
                ({
                  id,
                  image,
                  youtubeId,
                  video,
                  videoMobile,
                  heading,
                  body,
                  itemLink,
                  hasPlzTool,
                  roundImages,
                }) => {
                  const hasMobileVideo = videoMobile && videoMobile[0]

                  return (
                    <Fragment key={id}>
                      {image?.length > 0 && (
                        <div className="column-indent">
                          <img
                            src={image[0]?.url}
                            alt={heading}
                            className={`w-full h-auto mb-9 ${
                              roundImages
                                ? 'rounded shadow-tpmp lg:shadow-dp'
                                : ''
                            }`}
                            onLoad={() => {
                              if (newsItems.length === 1) recalculateParallax()
                            }}
                          />
                        </div>
                      )}
                      {video && video.length > 0 && (
                        <>
                          {isMobile && hasMobileVideo ? (
                            <video
                              width="100%"
                              height="auto"
                              muted
                              playsInline
                              autoPlay="autoplay"
                              fetchpriority="high"
                            >
                              <source
                                src={
                                  videoMobile.find(
                                    ({ extension }) => extension === 'mp4',
                                  )?.url
                                }
                                type='video/mp4; codecs="hvc1"'
                              />
                              <source
                                src={
                                  videoMobile.find(
                                    ({ extension }) => extension === 'webm',
                                  )?.url
                                }
                                type="video/webm"
                              />
                            </video>
                          ) : (
                            <motion.video
                              width="100%"
                              height="auto"
                              muted
                              playsInline
                              autoPlay="autoplay"
                              loop
                              className={`absolute top-0 left-0 object-cover ${
                                roundImages
                                  ? 'rounded shadow-tpmp lg:shadow-dp'
                                  : ''
                              }`}
                            >
                              <source
                                src={
                                  video.find(
                                    ({ extension }) => extension === 'mp4',
                                  )?.url
                                }
                                type='video/mp4; codecs="hvc1"'
                              />
                              <source
                                src={
                                  video.find(
                                    ({ extension }) => extension === 'webm',
                                  )?.url
                                }
                                type="video/webm"
                              />
                            </motion.video>
                          )}
                        </>
                      )}
                      {youtubeId && (
                        <iframe
                          title=""
                          src={`https://www.youtube.com/embed/${youtubeId}?modestbranding=1&rel=0`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className={`w-full aspect-video ${
                            roundImages
                              ? 'rounded shadow-tpmp lg:shadow-dp'
                              : ''
                          }`}
                        ></iframe>
                      )}
                      {newsItems.length > 1 && (
                        <div className={newsItems.length > 2 ? 'lg:mb-32' : ''}>
                          <h2 className="mb-4 u-h2 lg:mb-6 xl:mb-5">
                            {heading}
                          </h2>
                          {body && (
                            <div
                              className="mb-5 u-p column-indent lg:mb-4 xl:mb-6 link-wrapper"
                              dangerouslySetInnerHTML={{ __html: body }}
                            />
                          )}
                          {hasPlzTool && (
                            <div className="mb-5 column-indent">
                              <QueryClientProvider client={new QueryClient()}>
                                <InlinePlzTool />
                              </QueryClientProvider>
                            </div>
                          )}
                          {itemLink?.type && (
                            <div className="column-indent">
                              <DynamicLink link={itemLink} className="u-btn" />
                            </div>
                          )}
                        </div>
                      )}
                      {newsItems.length === 1 &&
                        newsItems[0].image.length === 0 && (
                          <h2 className="mb-4 u-h2 lg:mb-0">
                            {newsItems[0].heading}
                          </h2>
                        )}
                    </Fragment>
                  )
                },
              )}
          </motion.div>
          {newsItems.length === 1 && (
            <motion.div
              className={`col-2-type-e ${
                newsItems[0].image.length === 0 && 'sm:pt-14'
              }`}
              ref={ref2}
              style={{ y: yRef2 }}
            >
              {newsItems[0].image.length > 0 && (
                <h2 className="mb-4 u-h2 lg:mb-6 xl:mb-5">
                  {newsItems[0].heading}
                </h2>
              )}
              {newsItems[0].body && (
                <div
                  className="mb-5 u-p column-indent lg:mb-4 xl:mb-6 link-wrapper"
                  dangerouslySetInnerHTML={{ __html: newsItems[0].body }}
                />
              )}
              {newsItems[0].hasPlzTool && (
                <div className="mb-5 column-indent">
                  <QueryClientProvider client={new QueryClient()}>
                    <InlinePlzTool />
                  </QueryClientProvider>
                </div>
              )}
              {newsItems[0].itemLink?.type && (
                <div className="column-indent">
                  <DynamicLink link={newsItems[0].itemLink} className="u-btn" />
                </div>
              )}
            </motion.div>
          )}
          {newsItems.length > 1 && (
            <div className="col-2-type-a">
              {newsItems
                .filter((_, index) => index % 2 === 1)
                .map(
                  ({
                    image,
                    heading,
                    body,
                    itemLink,
                    hasPlzTool,
                    roundImages,
                  }) => (
                    <>
                      {image?.length > 0 && (
                        <div className="column-indent">
                          <img
                            src={image[0]?.url}
                            alt={heading}
                            className={`w-full h-auto mb-9 ${
                              roundImages
                                ? 'rounded shadow-tpmp lg:shadow-dp'
                                : ''
                            }`}
                          />
                        </div>
                      )}
                      <h2 className="mb-4 u-h2 lg:mb-6 xl:mb-5">{heading}</h2>
                      {body && (
                        <div
                          className="mb-5 u-p column-indent lg:mb-4 xl:mb-6 link-wrapper"
                          dangerouslySetInnerHTML={{ __html: body }}
                        />
                      )}
                      {hasPlzTool && (
                        <div className="mb-5 column-indent">
                          <QueryClientProvider client={new QueryClient()}>
                            <InlinePlzTool />
                          </QueryClientProvider>
                        </div>
                      )}
                      {itemLink?.type && (
                        <div className="column-indent">
                          <DynamicLink link={itemLink} className="u-btn" />
                        </div>
                      )}
                    </>
                  ),
                )}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default M02TeaserNews
